<template>
  <div
    v-if="!apiData"
    class="relative bg-gradient-to-br from-slate-50 via-white to-blue-50 border-b border-gray-100 p-8"
  >
    <div class="flex items-center justify-center">
      <div class="text-gray-500">Loading tenant information...</div>
    </div>
  </div>
  <div
    v-else
    class="relative bg-gradient-to-br from-slate-50 via-white to-blue-50 border-b border-gray-100"
  >
    <div class="absolute inset-0 bg-grid-pattern opacity-5"></div>

    <div class="relative px-8 pt-6 pb-4">
      <button
        @click="$emit('go-back')"
        class="group inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-all duration-200 hover:bg-white/50 rounded-lg"
      >
        <i
          class="material-icons mr-2 text-lg group-hover:-translate-x-1 transition-transform duration-200"
          >arrow_back</i
        >
        Back to Tenants
      </button>
    </div>

    <div class="relative px-8 pb-8">
      <div class="flex items-start justify-between">
        <div class="flex items-start space-x-6">
          <div class="relative">
            <div
              class="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center shadow-lg"
            >
              <span class="text-white font-bold text-2xl tracking-tight">{{
                getInitials(tenant.name)
              }}</span>
            </div>
            <div class="absolute -bottom-1 -right-1">
              <div
                class="w-6 h-6 bg-emerald-500 rounded-full border-3 border-white flex items-center justify-center"
              >
                <i class="material-icons text-white text-xs">check</i>
              </div>
            </div>
          </div>

          <div class="flex-1 min-w-0 pt-1">
            <div class="flex items-center space-x-4 mb-2">
              <h1 class="text-3xl font-bold text-gray-900 tracking-tight">
                {{ tenant.name || "N/A" }}
              </h1>
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border"
                :class="getSubscriptionStatusClasses()"
              >
                <i class="material-icons text-xs mr-1">{{
                  subscriptionStatusInfo.icon
                }}</i>
                {{ subscriptionStatusInfo.label }}
              </span>
            </div>

            <div class="flex items-center space-x-2 mb-4">
              <i class="material-icons text-gray-400 text-lg">language</i>
              <a
                :href="(apiData && apiData.domainUrl) || 'https://agentcis.com'"
                target="_blank"
                class="text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
              >
                {{ (apiData && apiData.domainUrl) || "N/A" }}
              </a>
              <i class="material-icons text-gray-300 text-sm">open_in_new</i>
            </div>

            <div class="space-y-3">
              <div class="flex items-center space-x-6 text-sm text-gray-600">
                <div class="flex items-center space-x-1">
                  <i class="material-icons text-gray-400 text-sm"
                    >calendar_today</i
                  >
                  <span
                    >Member since
                    {{ formatJoinDate(tenant.created_at) || "N/A" }}</span
                  >
                </div>
                <div class="flex items-center space-x-1">
                  <i class="material-icons text-gray-400 text-sm">business</i>
                  <span>{{ planName || "N/A" }}</span>
                </div>
              </div>

              <!-- Contact Information -->
              <div class="flex items-center space-x-8 text-sm">
                <!-- Email -->
                <div class="flex items-center space-x-2">
                  <i class="material-icons text-blue-500 text-sm">email</i>
                  <span class="text-gray-700">{{
                    tenant.company_email || "N/A"
                  }}</span>
                </div>

                <!-- Phone -->
                <div class="flex items-center space-x-2">
                  <i class="material-icons text-emerald-500 text-sm">phone</i>
                  <span class="text-gray-700">{{
                    formatPhone(tenant.company_phone) || "N/A"
                  }}</span>
                </div>

                <!-- Location -->
                <div class="flex items-center space-x-2">
                  <i class="material-icons text-purple-500 text-sm"
                    >location_on</i
                  >
                  <span class="text-gray-700">{{
                    tenant.country_name || "N/A"
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Quick Actions -->
        <div class="flex-shrink-0 ml-8">
          <div
            class="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-4 border border-gray-100 min-w-[250px]"
          >
            <h3
              class="text-sm font-semibold text-gray-900 mb-3 flex items-center"
            >
              <i class="material-icons text-gray-500 text-lg mr-2">bolt</i>
              Quick Actions
            </h3>
            <div class="space-y-2">
              <button
                class="w-full flex items-center space-x-2 px-3 py-2 text-sm bg-white border border-gray-200 hover:border-purple-300 rounded-lg transition-all group opacity-60 cursor-not-allowed"
                :disabled="true"
              >
                <i class="material-icons text-gray-400 text-sm">email</i>
                <span class="text-gray-500">Send Email</span>
              </button>
              <a
                :href="
                  `https://agentcis.chargebee.com/d/subscriptions/${subscription.id}`
                "
                target="_blank"
                class="w-full flex items-center space-x-2 px-3 py-2 text-sm bg-white hover:bg-emerald-50 border border-gray-200 hover:border-emerald-300 rounded-lg transition-all group"
              >
                <i class="material-icons text-emerald-500 text-sm">settings</i>
                <span class="text-gray-700 group-hover:text-emerald-700"
                  >Manage Account</span
                >
              </a>
              <a
                :href="
                  `https://agentcis.chargebee.com/d/subscriptions/${subscription.id}`
                "
                target="_blank"
                class="w-full flex items-center space-x-2 px-3 py-2 text-sm bg-white hover:bg-amber-50 border border-gray-200 hover:border-amber-300 rounded-lg transition-all group"
              >
                <i class="material-icons text-amber-500 text-sm">receipt</i>
                <span class="text-gray-700 group-hover:text-amber-700"
                  >View Billing</span
                >
              </a>
              <button
                class="w-full flex items-center space-x-2 px-3 py-2 text-sm bg-white border border-gray-200 hover:border-purple-300 rounded-lg transition-all group opacity-60 cursor-not-allowed"
                :disabled="true"
              >
                <i class="material-icons text-purple-500 text-sm">note_add</i>
                <span class="text-gray-700 group-hover:text-purple-700"
                  >Add Notes</span
                >
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Premium Stats Grid -->
    <div class="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
      <!-- Subscription Card -->
      <div
        class="rounded-2xl p-6 border relative overflow-hidden"
        :class="getSubscriptionCardClasses()"
      >
        <div
          class="absolute top-0 right-0 w-20 h-20 rounded-full -translate-y-10 translate-x-10"
          :class="getSubscriptionCardAccent()"
        ></div>
        <div class="relative">
          <div class="flex items-center space-x-3 mb-4">
            <div
              class="w-10 h-10 rounded-xl flex items-center justify-center"
              :class="getSubscriptionIconClasses()"
            >
              <i class="material-icons text-white text-lg">{{
                subscriptionStatusInfo.icon
              }}</i>
            </div>
            <div>
              <h3
                class="text-sm font-semibold"
                :class="getSubscriptionTextColor()"
              >
                Subscription
              </h3>
              <p class="text-xs" :class="getSubscriptionSubtextColor()">
                {{ planName }}
              </p>
            </div>
          </div>

          <!-- Cancellation Warning -->
          <div
            v-if="isScheduledForCancellation"
            class="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg"
          >
            <div class="flex items-center space-x-2 mb-2">
              <i class="material-icons text-red-600 text-sm">warning</i>
              <span class="text-sm font-semibold text-red-900"
                >Cancellation Scheduled</span
              >
            </div>
            <p class="text-xs text-red-700">
              This subscription will be cancelled on
              {{ formatDate(cancellationDate) }}
            </p>
          </div>

          <!-- Billing Information -->
          <div class="mb-4">
            <p class="text-xs mb-1" :class="getSubscriptionSubtextColor()">
              {{
                isScheduledForCancellation ? "Final Billing" : "Next Billing"
              }}
            </p>
            <div class="flex items-baseline space-x-1">
              <span
                class="text-2xl font-bold"
                :class="getSubscriptionTextColor()"
              >
                {{ currency }}
              </span>
              <span
                class="text-3xl font-bold"
                :class="getSubscriptionTextColor()"
              >
                {{ nextBillingAmount }}
              </span>
            </div>
            <p class="text-xs mt-1" :class="getSubscriptionSubtextColor()">
              Due {{ formatShortDate(nextBillingDate) || "N/A" }}
            </p>
          </div>

          <!-- Subscription Status -->
          <div
            class="flex items-center space-x-2 text-xs"
            :class="getSubscriptionSubtextColor()"
          >
            <i
              class="material-icons text-sm"
              :class="getSubscriptionAccentColor()"
              style="font-size: 14px;"
            >
              {{ getSubscriptionStatusIcon() }}
            </i>
            <span>{{ getSubscriptionStatusText() }}</span>
          </div>
        </div>
      </div>

      <!-- Users Card -->
      <div
        class="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100/50 relative overflow-hidden"
      >
        <div
          class="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-full -translate-y-8 translate-x-8"
        ></div>
        <div class="relative">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center space-x-3">
              <div
                class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center"
              >
                <i class="material-icons text-emerald-600 text-lg">groups</i>
              </div>
              <div>
                <h3 class="text-sm font-semibold text-emerald-900">Users</h3>
                <p class="text-xs text-emerald-600">Active members</p>
              </div>
            </div>
          </div>

          <!-- Circular Progress -->
          <div class="flex items-center space-x-4">
            <div class="relative w-16 h-16">
              <svg class="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#d1fae5"
                  stroke-width="3"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  :stroke="
                    userUtilizationPercentage > 100 ? '#ef4444' : '#10b981'
                  "
                  stroke-width="3"
                  :stroke-dasharray="
                    `${Math.min(userUtilizationPercentage, 100)}, 100`
                  "
                />
              </svg>
              <div class="absolute inset-0 flex items-center justify-center">
                <span class="text-sm font-bold text-emerald-900">{{
                  stats.activeUsers
                }}</span>
              </div>
            </div>
            <div>
              <p class="text-2xl font-bold text-emerald-900">
                {{ stats.activeUsers }}
              </p>
              <p class="text-xs text-emerald-600">of {{ userLimit }} allowed</p>
              <p
                class="text-xs mt-1"
                :class="
                  userUtilizationPercentage > 100
                    ? 'text-red-500'
                    : 'text-emerald-500'
                "
              >
                {{ userUtilizationRounded }}% of limit
                <span
                  v-if="userUtilizationPercentage > 100"
                  class="font-semibold"
                  >(Over limit)</span
                >
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Storage Card -->
      <div
        class="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-100/50 relative overflow-hidden"
      >
        <div
          class="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full -translate-y-8 translate-x-8"
        ></div>
        <div class="relative">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center space-x-3">
              <div
                class="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center"
              >
                <i class="material-icons text-amber-600 text-lg"
                  >cloud_upload</i
                >
              </div>
              <div>
                <h3 class="text-sm font-semibold text-amber-900">Storage</h3>
                <p class="text-xs text-amber-600">Data usage</p>
              </div>
            </div>
            <!-- Usage Badge -->
            <div class="flex items-center space-x-1">
              <div
                class="px-2 py-1 rounded-full text-xs font-medium"
                :class="
                  getStorageStatusClass(stats.usedStorage, stats.totalStorage)
                "
              >
                {{ storageUtilizationRounded }}%
              </div>
            </div>
          </div>

          <!-- Visual Progress Ring -->
          <div class="flex items-center space-x-4 mb-3">
            <div class="relative w-20 h-20">
              <svg class="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#fed7aa"
                  stroke-width="2.5"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  :stroke="
                    getStorageColor(stats.usedStorage, stats.totalStorage)
                  "
                  stroke-width="2.5"
                  stroke-linecap="round"
                  :stroke-dasharray="`${storageUtilizationPercentage}, 100`"
                />
              </svg>
              <div
                class="absolute inset-0 flex flex-col items-center justify-center"
              >
                <span class="text-lg font-bold text-amber-900">{{
                  storageUsedSmart.value
                }}</span>
                <span class="text-xs text-amber-600">{{
                  storageUsedSmart.unit
                }}</span>
              </div>
            </div>
            <div class="flex-1">
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-amber-900">Used</span>
                  <span class="text-sm font-bold text-amber-900"
                    >{{ storageUsedSmart.value }}
                    {{ storageUsedSmart.unit }}</span
                  >
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-amber-700">Available</span>
                  <span class="text-sm text-amber-700"
                    >{{ storageAvailableSmart.value }}
                    {{ storageAvailableSmart.unit }}</span
                  >
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-amber-600">Total</span>
                  <span class="text-sm text-amber-600"
                    >{{ storageTotalSmart.value }}
                    {{ storageTotalSmart.unit }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Storage Warning -->
          <div
            v-if="storageUtilizationPercentage > 80"
            class="flex items-center space-x-2 text-xs text-orange-700 bg-orange-100 rounded-lg p-2"
          >
            <i class="material-icons text-orange-600" style="font-size: 14px;"
              >warning</i
            >
            <span>Storage is {{ storageUtilizationRounded }}% full</span>
          </div>
        </div>
      </div>

      <!-- Email Usage Card -->
      <div
        class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100/50 relative overflow-hidden"
      >
        <div
          class="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full -translate-y-8 translate-x-8"
        ></div>
        <div class="relative">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center space-x-3">
              <div
                class="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center"
              >
                <i class="material-icons text-white text-lg">email</i>
              </div>
              <div>
                <h3 class="text-sm font-semibold text-blue-900">Email Usage</h3>
                <p class="text-xs text-blue-600">Monthly limits</p>
              </div>
            </div>
            <!-- Overall Usage Badge -->
            <div class="flex items-center space-x-1">
              <div
                class="px-2 py-1 rounded-full text-xs font-medium"
                :class="getEmailStatusClass(emailTotalUsage, emailTotalLimit)"
              >
                {{ emailUtilizationRounded }}%
              </div>
            </div>
          </div>

          <!-- Visual Email Stats -->
          <div class="grid grid-cols-2 gap-4 mb-3">
            <!-- Inbox Visual -->
            <div class="bg-blue-100/50 rounded-xl p-3">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center space-x-2">
                  <div
                    class="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center"
                  >
                    <i class="material-icons text-white text-sm">inbox</i>
                  </div>
                  <span class="text-sm font-medium text-blue-900">Inbox</span>
                </div>
                <span class="text-xs font-bold text-blue-700"
                  >{{ Math.round((emailInbox / emailInboxLimit) * 100) }}%</span
                >
              </div>

              <!-- Circular Progress for Inbox -->
              <div class="flex items-center space-x-3">
                <div class="relative w-12 h-12">
                  <svg
                    class="w-12 h-12 transform -rotate-90"
                    viewBox="0 0 36 36"
                  >
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#bfdbfe"
                      stroke-width="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#3b82f6"
                      stroke-width="3"
                      stroke-linecap="round"
                      :stroke-dasharray="
                        `${(emailInbox / emailInboxLimit) * 100}, 100`
                      "
                    />
                  </svg>
                  <div
                    class="absolute inset-0 flex items-center justify-center"
                  >
                    <span class="text-xs font-bold text-blue-900">{{
                      emailInbox
                    }}</span>
                  </div>
                </div>
                <div class="flex-1">
                  <p class="text-xs text-blue-700">
                    {{ emailInbox }} / {{ emailInboxLimit }}
                  </p>
                  <p class="text-xs text-blue-600">
                    {{ emailInboxLimit - emailInbox }} remaining
                  </p>
                </div>
              </div>
            </div>

            <!-- Outbox Visual -->
            <div class="bg-indigo-100/50 rounded-xl p-3">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center space-x-2">
                  <div
                    class="w-6 h-6 bg-indigo-500 rounded-lg flex items-center justify-center"
                  >
                    <i class="material-icons text-white text-sm">outbox</i>
                  </div>
                  <span class="text-sm font-medium text-blue-900">Outbox</span>
                </div>
                <span class="text-xs font-bold text-indigo-700"
                  >{{
                    Math.round((emailOutbox / emailOutboxLimit) * 100)
                  }}%</span
                >
              </div>

              <!-- Circular Progress for Outbox -->
              <div class="flex items-center space-x-3">
                <div class="relative w-12 h-12">
                  <svg
                    class="w-12 h-12 transform -rotate-90"
                    viewBox="0 0 36 36"
                  >
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#c7d2fe"
                      stroke-width="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#6366f1"
                      stroke-width="3"
                      stroke-linecap="round"
                      :stroke-dasharray="
                        `${(emailOutbox / emailOutboxLimit) * 100}, 100`
                      "
                    />
                  </svg>
                  <div
                    class="absolute inset-0 flex items-center justify-center"
                  >
                    <span class="text-xs font-bold text-indigo-900">{{
                      emailOutbox
                    }}</span>
                  </div>
                </div>
                <div class="flex-1">
                  <p class="text-xs text-indigo-700">
                    {{ emailOutbox }} / {{ emailOutboxLimit }}
                  </p>
                  <p class="text-xs text-indigo-600">
                    {{ emailOutboxLimit - emailOutbox }} remaining
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Email Usage Warning -->
          <div
            v-if="emailUtilizationPercentage > 85"
            class="flex items-center space-x-2 text-xs text-red-700 bg-red-100 rounded-lg p-2"
          >
            <i class="material-icons text-red-600" style="font-size: 14px;"
              >warning</i
            >
            <span
              >High email usage - {{ emailUtilizationRounded }}% of limit</span
            >
          </div>

          <!-- Email Tip -->
          <div
            v-else
            class="flex items-center space-x-2 text-xs text-blue-700 bg-blue-100/50 rounded-lg p-2"
          >
            <i class="material-icons text-blue-600" style="font-size: 14px;"
              >info</i
            >
            <span
              >{{ emailTotalLimit - emailTotalUsage }} emails remaining this
              month</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TenantHeader",
  props: {
    apiData: {
      type: Object,
      default: () => null
    }
  },
  computed: {
    tenant() {
      return (this.apiData && this.apiData.tenantCompanyDetails) || {};
    },
    subscription() {
      return (this.apiData && this.apiData.subscription) || {};
    },
    emailUsage() {
      return (this.apiData && this.apiData.emailUsage) || [];
    },
    storageUsage() {
      return (this.apiData && this.apiData.storageUsage) || [];
    },
    stats() {
      if (!this.apiData) {
        return {
          activeUsers: 0,
          totalUsers: 0,
          usedStorage: 0,
          totalStorage: 0
        };
      }

      const storageData = this.storageUsage[0];
      const storageAddon =
        this.subscription.add_ons &&
        this.subscription.add_ons.find(addon => addon.id === "storage");

      return {
        activeUsers: this.apiData.tenantActiveUsersCount || 0,
        totalUsers: this.apiData.tenantTotalUsersCount || 0,
        usedStorageBytes: storageData ? storageData.value : 0, // Store raw bytes
        usedStorage: storageData ? storageData.value / (1024 * 1024 * 1024) : 0, // Keep GB for calculations
        totalStorage: (storageAddon && storageAddon.quantity) || 0
      };
    },
    emailInbox() {
      const emailInboxData = this.emailUsage.find(
        email => email.feature_name === "email-inbox"
      );
      return (emailInboxData && emailInboxData.value) || 0;
    },
    emailOutbox() {
      const emailOutboxData = this.emailUsage.find(
        email => email.feature_name === "email-outbox"
      );
      return (emailOutboxData && emailOutboxData.value) || 0;
    },
    emailInboxLimit() {
      const emailInboxAddon =
        this.subscription.add_ons &&
        this.subscription.add_ons.find(addon => addon.id === "email-inbox");
      return (emailInboxAddon && emailInboxAddon.quantity) || 0;
    },
    emailOutboxLimit() {
      const emailOutboxAddon =
        this.subscription.add_ons &&
        this.subscription.add_ons.find(addon => addon.id === "email-outbox");
      return (emailOutboxAddon && emailOutboxAddon.quantity) || 0;
    },
    planName() {
      return this.subscription.plan_name || "N/A";
    },
    subscriptionStatus() {
      return (this.apiData && this.apiData.tenantSubscriptionStatus) || "N/A";
    },
    nextBillingAmount() {
      return (
        (this.subscription.next_billing_amount &&
          this.subscription.next_billing_amount.toLocaleString()) ||
        "0"
      );
    },
    nextBillingDate() {
      return this.subscription.next_billing_at || null;
    },
    currency() {
      return this.subscription.currency_cde || "USD";
    },
    isScheduledForCancellation() {
      return (
        this.subscription.cancelled_at &&
        new Date(this.subscription.cancelled_at) > new Date()
      );
    },
    cancellationDate() {
      return this.subscription.cancelled_at;
    },
    userLimit() {
      const userAddon =
        this.subscription.add_ons &&
        this.subscription.add_ons.find(addon => addon.id === "users");
      return (userAddon && userAddon.quantity) || this.stats.totalUsers || 1;
    },
    userUtilizationPercentage() {
      return (this.stats.activeUsers / this.userLimit) * 100;
    },
    userUtilizationRounded() {
      return Math.round(this.userUtilizationPercentage);
    },
    storageUtilizationPercentage() {
      return (this.stats.usedStorage / this.stats.totalStorage) * 100;
    },
    storageUtilizationRounded() {
      return Math.round(this.storageUtilizationPercentage);
    },
    storageUsedFormatted() {
      return this.stats.usedStorage.toFixed(2);
    },
    storageAvailable() {
      return Math.max(0, this.stats.totalStorage - this.stats.usedStorage);
    },
    storageAvailableFormatted() {
      return this.storageAvailable.toFixed(2);
    },
    // Smart storage formatting - show MB if < 1GB, otherwise GB
    storageUsedSmart() {
      const usedGB = this.stats.usedStorage; // This now has full precision
      if (usedGB < 1) {
        const usedMB = usedGB * 1024;
        return {
          value: usedMB.toFixed(1), // Show 1 decimal for MB
          unit: "MB"
        };
      }
      return {
        value: usedGB.toFixed(2),
        unit: "GB"
      };
    },
    storageAvailableSmart() {
      const availableGB = this.storageAvailable;
      if (availableGB < 1) {
        const availableMB = availableGB * 1024;
        return {
          value: availableMB.toFixed(0),
          unit: "MB"
        };
      }
      return {
        value: availableGB.toFixed(2),
        unit: "GB"
      };
    },
    storageTotalSmart() {
      const totalGB = this.stats.totalStorage;
      if (totalGB < 1) {
        const totalMB = totalGB * 1024;
        return {
          value: totalMB.toFixed(0),
          unit: "MB"
        };
      }
      return {
        value: totalGB.toFixed(2),
        unit: "GB"
      };
    },
    emailTotalUsage() {
      return this.emailInbox + this.emailOutbox;
    },
    emailTotalLimit() {
      return this.emailInboxLimit + this.emailOutboxLimit;
    },
    emailUtilizationPercentage() {
      return (this.emailTotalUsage / this.emailTotalLimit) * 100;
    },
    emailUtilizationRounded() {
      return Math.round(this.emailUtilizationPercentage);
    },
    subscriptionStatusInfo() {
      const IN_TRIAL = 1;
      const ACTIVE = 2;
      const NON_RENEWING = 3;
      const CANCELLED = 4;

      if (this.isScheduledForCancellation) {
        return {
          label: "Cancellation Scheduled",
          color: "red",
          icon: "event_busy"
        };
      }

      const status = this.subscription.status;

      switch (status) {
        case IN_TRIAL:
          return {
            label: "In Trial",
            color: "blue",
            icon: "access_time"
          };
        case ACTIVE:
          return {
            label: "Active",
            color: "green",
            icon: "check_circle"
          };
        case NON_RENEWING:
          return {
            label: "Non Renewing",
            color: "orange",
            icon: "schedule"
          };
        case CANCELLED:
          return {
            label: "Cancelled",
            color: "red",
            icon: "cancel"
          };
        default:
          return {
            label: this.subscriptionStatus || "Unknown",
            color: "gray",
            icon: "help"
          };
      }
    }
  },

  methods: {
    getInitials(name) {
      if (!name) return "GI";
      return name
        .split(" ")
        .map(word => word.charAt(0))
        .join("")
        .toUpperCase()
        .substring(0, 2);
    },
    formatDate(dateString) {
      if (!dateString) return null;
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit"
        });
      } catch (error) {
        return dateString;
      }
    },
    formatJoinDate(dateString) {
      if (!dateString) return null;
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long"
        });
      } catch (error) {
        return dateString;
      }
    },
    formatPhone(phone) {
      if (!phone) return null;
      // Format phone number for better readability
      if (phone.startsWith("+977")) {
        return phone.replace(/(\+977)(\d{3})(\d{3})(\d{4})/, "$1 $2-$3-$4");
      }
      if (phone.startsWith("+61")) {
        return phone.replace(/(\+61)(\d{2})(\d{4})(\d{3})/, "$1 $2 $3 $4");
      }
      return phone;
    },
    formatShortDate(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric"
        });
      } catch (error) {
        return dateString;
      }
    },
    getStorageStatusClass(used, total) {
      const percentage = (used / total) * 100;
      if (percentage >= 90) return "bg-red-200 text-red-800";
      if (percentage >= 80) return "bg-orange-200 text-orange-800";
      if (percentage >= 60) return "bg-yellow-200 text-yellow-800";
      return "bg-green-200 text-green-800";
    },
    getStorageColor(used, total) {
      const percentage = (used / total) * 100;
      if (percentage >= 90) return "#dc2626";
      if (percentage >= 80) return "#ea580c";
      if (percentage >= 60) return "#d97706";
      return "#059669";
    },
    getEmailStatusClass(used, total) {
      const percentage = (used / total) * 100;
      if (percentage >= 90) return "bg-red-200 text-red-800";
      if (percentage >= 85) return "bg-orange-200 text-orange-800";
      if (percentage >= 70) return "bg-yellow-200 text-yellow-800";
      return "bg-green-200 text-green-800";
    },
    // Subscription status styling methods
    getSubscriptionStatusClasses() {
      const colorMap = {
        red: "bg-red-100 text-red-800 border-red-200",
        orange: "bg-orange-100 text-orange-800 border-orange-200",
        green: "bg-emerald-100 text-emerald-800 border-emerald-200",
        blue: "bg-blue-100 text-blue-800 border-blue-200",
        gray: "bg-gray-100 text-gray-800 border-gray-200"
      };
      return colorMap[this.subscriptionStatusInfo.color] || colorMap.gray;
    },
    getSubscriptionCardClasses() {
      const color = this.subscriptionStatusInfo.color;

      if (this.isScheduledForCancellation || color === "red") {
        return "bg-gradient-to-br from-red-50 to-pink-50 border-red-100/50";
      }
      if (color === "orange") {
        return "bg-gradient-to-br from-orange-50 to-amber-50 border-orange-100/50";
      }
      if (color === "blue") {
        return "bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100/50";
      }
      if (color === "green") {
        return "bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100/50";
      }
      return "bg-gradient-to-br from-gray-50 to-slate-50 border-gray-100/50";
    },
    getSubscriptionCardAccent() {
      const color = this.subscriptionStatusInfo.color;

      if (this.isScheduledForCancellation || color === "red") {
        return "bg-gradient-to-br from-red-400/20 to-pink-400/20";
      }
      if (color === "orange") {
        return "bg-gradient-to-br from-orange-400/20 to-amber-400/20";
      }
      if (color === "blue") {
        return "bg-gradient-to-br from-blue-400/20 to-indigo-400/20";
      }
      if (color === "green") {
        return "bg-gradient-to-br from-emerald-400/20 to-green-400/20";
      }
      return "bg-gradient-to-br from-gray-400/20 to-slate-400/20";
    },
    getSubscriptionIconClasses() {
      const color = this.subscriptionStatusInfo.color;

      if (this.isScheduledForCancellation || color === "red") {
        return "bg-gradient-to-br from-red-500 to-pink-500";
      }
      if (color === "orange") {
        return "bg-gradient-to-br from-orange-500 to-amber-500";
      }
      if (color === "blue") {
        return "bg-gradient-to-br from-blue-500 to-indigo-500";
      }
      if (color === "green") {
        return "bg-gradient-to-br from-emerald-500 to-green-500";
      }
      return "bg-gradient-to-br from-gray-500 to-slate-500";
    },
    getSubscriptionTextColor() {
      const color = this.subscriptionStatusInfo.color;

      if (this.isScheduledForCancellation || color === "red") {
        return "text-red-900";
      }
      if (color === "orange") {
        return "text-orange-900";
      }
      if (color === "blue") {
        return "text-blue-900";
      }
      if (color === "green") {
        return "text-emerald-900";
      }
      return "text-gray-900";
    },
    getSubscriptionSubtextColor() {
      const color = this.subscriptionStatusInfo.color;

      if (this.isScheduledForCancellation || color === "red") {
        return "text-red-700";
      }
      if (color === "orange") {
        return "text-orange-700";
      }
      if (color === "blue") {
        return "text-blue-700";
      }
      if (color === "green") {
        return "text-emerald-700";
      }
      return "text-gray-700";
    },
    getSubscriptionAccentColor() {
      const color = this.subscriptionStatusInfo.color;

      if (this.isScheduledForCancellation || color === "red") {
        return "text-red-500";
      }
      if (color === "orange") {
        return "text-orange-500";
      }
      if (color === "blue") {
        return "text-blue-500";
      }
      if (color === "green") {
        return "text-emerald-500";
      }
      return "text-gray-500";
    },
    getSubscriptionStatusIcon() {
      const color = this.subscriptionStatusInfo.color;

      if (this.isScheduledForCancellation) {
        return "event_busy";
      }
      if (color === "orange") {
        // Non renewing
        return "schedule";
      }
      if (color === "blue") {
        // In trial
        return "access_time";
      }
      if (color === "green") {
        // Active
        return "schedule";
      }
      if (color === "red") {
        // Cancelled
        return "cancel";
      }
      return "help";
    },
    getSubscriptionStatusText() {
      const color = this.subscriptionStatusInfo.color;

      if (this.isScheduledForCancellation) {
        return "Cancellation scheduled";
      }
      if (color === "orange") {
        // Non renewing
        return "Will not auto-renew";
      }
      if (color === "blue") {
        // In trial
        return "Trial period active";
      }
      if (color === "green") {
        // Active
        return "Auto-renew enabled";
      }
      if (color === "red") {
        // Cancelled
        return "Subscription cancelled";
      }
      return "Status unknown";
    }
  }
};
</script>

<style scoped>
.material-icons.md-24 {
  font-size: 24px;
}

.bg-grid-pattern {
  background-image: radial-gradient(
    circle at 1px 1px,
    rgba(79, 70, 229, 0.1) 1px,
    transparent 0
  );
  background-size: 20px 20px;
}

.border-3 {
  border-width: 3px;
}

/* Smooth hover animations */
.group:hover .group-hover\:bg-blue-100 {
  background-color: rgb(219 234 254);
}

.group:hover .group-hover\:bg-emerald-100 {
  background-color: rgb(209 250 229);
}

.group:hover .group-hover\:bg-purple-100 {
  background-color: rgb(243 232 255);
}
</style>
